/**
 * PreviewRenderer Class
 * Handles real-time canvas rendering of engraved text on bottle image
 * WITH RESPONSIVE COORDINATE SCALING AND CONFIGURABLE IMAGE SIZES
 */
class PreviewRenderer {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.bottleImage = null;
    this.baseImageHeight = 280;  // Reference height for coordinate settings (mobile default)
    this.settings = {
      textAreaX: 62,
      textAreaY: 135,
      textAreaWidth: 95,
      textAreaHeight: 55,
      fontSize: 16,
      style1Font: 'Georgia',
      style2Font: 'Arial',
      color1Hex: '#000000',
      color2Hex: '#8B4513',
      color3Hex: '#C0C0C0'
    };
  }

  /**
   * Initialize the preview renderer
   * @param {HTMLCanvasElement} canvasElement - The canvas element
   */
  init(canvasElement) {
    if (!canvasElement) {
      console.warn('PreviewRenderer: Canvas element not found');
      return false;
    }

    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext('2d');
    
    // Load settings from data attributes
    this.loadSettings();
    
    // Find the bottle image
    this.bottleImage = this.canvas.previousElementSibling;
    
    if (!this.bottleImage || this.bottleImage.tagName !== 'IMG') {
      console.warn('PreviewRenderer: Bottle image not found');
      return false;
    }
    
    // Setup canvas dimensions
    this.setupCanvas();
    
    console.log('PreviewRenderer initialized with settings:', this.settings);
    console.log('Base height for coordinates:', this.baseImageHeight);
    return true;
  }

  /**
   * Load settings from canvas data attributes
   */
  loadSettings() {
  if (!this.canvas) return;

  const dataset = this.canvas.dataset;
  
  this.settings.textAreaX = parseInt(dataset.textX) || 62;
  this.settings.textAreaY = parseInt(dataset.textY) || 135;
  this.settings.textAreaWidth = parseInt(dataset.textWidth) || 95;
  this.settings.textAreaHeight = parseInt(dataset.textHeight) || 55;
  this.settings.fontSize = parseInt(dataset.fontSize) || 16;
  this.settings.style1Font = dataset.style1Font || 'Georgia';
  this.settings.style2Font = dataset.style2Font || 'Arial';
  this.settings.style1Bold = dataset.style1Bold === 'true';
  this.settings.style1Italic = dataset.style1Italic === 'true';
  this.settings.style2Bold = dataset.style2Bold === 'true';
  this.settings.style2Italic = dataset.style2Italic === 'true';
  this.settings.color1Hex = dataset.color1 || '#000000';
  this.settings.color2Hex = dataset.color2 || '#8B4513';
  this.settings.color3Hex = dataset.color3 || '#C0C0C0';
  
  this.baseImageHeight = parseInt(dataset.baseHeight) || 280;
}

  /**
   * Setup canvas dimensions with DPI scaling
   */
  setupCanvas() {
    if (!this.canvas || !this.bottleImage) return;

    // Check if image is already loaded
    if (this.bottleImage.complete && this.bottleImage.naturalWidth > 0) {
      this.setCanvasDimensions();
    } else {
      // Wait for image to load
      this.bottleImage.addEventListener('load', () => {
        this.setCanvasDimensions();
      });
      
      // Also try after a short delay in case the load event already fired
      setTimeout(() => {
        if (this.bottleImage.complete && this.bottleImage.naturalWidth > 0) {
          this.setCanvasDimensions();
        }
      }, 100);
    }
    
    // Re-setup canvas on window resize (for responsive breakpoints)
    window.addEventListener('resize', () => {
      this.setCanvasDimensions();
    });
  }

  /**
   * Set canvas dimensions to match bottle image
   */
  setCanvasDimensions() {
    if (!this.canvas || !this.bottleImage) return;

    // Get actual displayed dimensions
    const width = this.bottleImage.offsetWidth || this.bottleImage.clientWidth;
    const height = this.bottleImage.offsetHeight || this.bottleImage.clientHeight;
    
    // Fallback to natural dimensions if displayed dimensions are 0
    const finalWidth = width > 0 ? width : this.bottleImage.naturalWidth;
    const finalHeight = height > 0 ? height : this.bottleImage.naturalHeight;

    if (finalWidth === 0 || finalHeight === 0) {
      console.warn('PreviewRenderer: Unable to determine image dimensions, retrying...');
      setTimeout(() => this.setCanvasDimensions(), 200);
      return;
    }

    const dpr = window.devicePixelRatio || 1;

    // Set display size (css pixels)
    this.canvas.style.width = finalWidth + 'px';
    this.canvas.style.height = finalHeight + 'px';

    // Set actual size in memory (scaled for DPI)
    this.canvas.width = finalWidth * dpr;
    this.canvas.height = finalHeight * dpr;

    // Scale context to match DPI
    this.ctx.scale(dpr, dpr);

    console.log(`Canvas dimensions set: ${finalWidth}x${finalHeight} (DPR: ${dpr})`);
  }

  /**
   * Calculate scaled coordinates based on current image size
   * @returns {Object} Scaled coordinates
   */
  getScaledCoordinates() {
    if (!this.bottleImage) {
      return {
        x: this.settings.textAreaX,
        y: this.settings.textAreaY,
        width: this.settings.textAreaWidth,
        height: this.settings.textAreaHeight,
        fontSize: this.settings.fontSize
      };
    }

    // Get current displayed height
    const currentHeight = this.bottleImage.offsetHeight || this.bottleImage.clientHeight || this.baseImageHeight;

    // Calculate scale factor based on height (mobile = base reference)
    const scale = currentHeight / this.baseImageHeight;

    return {
      x: this.settings.textAreaX * scale,
      y: this.settings.textAreaY * scale,
      width: this.settings.textAreaWidth * scale,
      height: this.settings.textAreaHeight * scale,
      fontSize: this.settings.fontSize * scale
    };
  }

  /**
   * Render text on canvas
   * @param {Object} options - Rendering options
   * @param {string} options.text - Text to render
   * @param {string} options.fontFamily - Font family to use
   * @param {string} options.color - Hex color code
   */
  render({ text, fontFamily, color }) {
  if (!this.ctx) {
    console.warn('PreviewRenderer: Context not initialized');
    return;
  }

  if (this.canvas.width === 0 || this.canvas.height === 0) {
    this.setCanvasDimensions();
    if (this.canvas.width === 0 || this.canvas.height === 0) {
      setTimeout(() => this.render({ text, fontFamily, color }), 100);
      return;
    }
  }

  this.clear();

  if (!text || text.trim() === '') {
    return;
  }

  const scaled = this.getScaledCoordinates();

  // Build font string with bold and italic
  let fontStyle = '';
  let fontWeight = 'normal';
  
  // Determine which style is being used
  const isStyle1 = fontFamily === this.settings.style1Font;
  const isStyle2 = fontFamily === this.settings.style2Font;
  
  if (isStyle1) {
    if (this.settings.style1Italic) fontStyle = 'italic';
    if (this.settings.style1Bold) fontWeight = 'bold';
  } else if (isStyle2) {
    if (this.settings.style2Italic) fontStyle = 'italic';
    if (this.settings.style2Bold) fontWeight = 'bold';
  }
  
  // Construct font string: [style] [weight] size family
  const fontString = `${fontStyle} ${fontWeight} ${scaled.fontSize}px ${fontFamily}`.trim();

  this.ctx.font = fontString;
  this.ctx.fillStyle = color;
  this.ctx.textAlign = 'center';
  this.ctx.textBaseline = 'middle';

  const centerX = scaled.x + (scaled.width / 2);
  const centerY = scaled.y + (scaled.height / 2);

  this.ctx.fillText(text, centerX, centerY);

  console.log(`Rendered: "${text}" at (${centerX.toFixed(1)}, ${centerY.toFixed(1)}) with ${fontString} in ${color} (scale: ${(scaled.fontSize / this.settings.fontSize).toFixed(2)})`);
}

  /**
   * Clear the canvas
   */
  clear() {
    if (!this.ctx || !this.canvas) return;
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Get current settings (for debugging)
   */
  getSettings() {
    return { ...this.settings };
  }
}

// Export for use in main popup script
window.PreviewRenderer = PreviewRenderer;
